// Role registry — 39 roles across 3 factions.
//
// To add a new role:
//   1. Drop a new `<id>.ts` file in this folder exporting a RoleConfig.
//   2. Add it to ALL_ROLES below.
//   3. (optional) Add an SVG illustration in client/src/components/RoleArt.tsx.
//      Roles without one fall back to a generic glyph using their latin code.
//
// If the role uses an existing ActionVerb, the engine handles it — the night
// resolver (server/src/room/night.ts) dispatches on the verb plus the
// AbilityConfig flags, never on role ids. The contract:
//   - `kill`            — joins the queue named by `killGroup`: 'mafia' votes
//                         into the team tally (commander tie-break),
//                         'independent' queues its own kill. Omitted =
//                         faction default (mafia roles → 'mafia', everyone
//                         else → 'independent'), so a new killer resolves
//                         either way.
//   - `distract` / `silence` / `jail` — roleblocks; `unblockable: true`
//                         makes the block fire even when the blocker is
//                         themselves blocked (strangler vs saboteur is
//                         exactly this one flag).
//   - `rampage`, `ignite`, `douse`, `execute`, and every info/support verb —
//                         dispatched purely by verb; any role declaring one
//                         gets the standard resolution.
//   - Passive ROLE-level flags the resolver/vote reads: `cleansVictims`
//                         (janitor), `voteWeight` (mayor, after reveal),
//                         `nightFilter` (werewolf odd nights), `seniority`
//                         (mafia commander succession).
// New VERBS still require resolver surgery: add to ActionVerb in types.ts,
// teach resolveNight()/resolveDayAction() the new step, and add prompt copy
// in client/src/screens/Night.tsx.

import type { RoleConfig } from '../types.js';

import { civilian } from './civilian.js';
import { sheriff } from './sheriff.js';
import { doctor } from './doctor.js';
import { lawyer } from './lawyer.js';
import { bodyguard } from './bodyguard.js';
import { mayor } from './mayor.js';
import { vigilante } from './vigilante.js';
import { busDriver } from './bus_driver.js';
import { veteran } from './veteran.js';
import { spy } from './spy.js';
import { watcher } from './watcher.js';
import { priest } from './priest.js';
import { detective } from './detective.js';
import { escort } from './escort.js';
import { monk } from './monk.js';
import { coroner } from './coroner.js';
import { jailer } from './jailer.js';
import { tracker } from './tracker.js';

import { don } from './don.js';
import { goon } from './goon.js';
import { consigliere } from './consigliere.js';
import { framer } from './framer.js';
import { janitor } from './janitor.js';
import { hitman } from './hitman.js';
import { saboteur } from './saboteur.js';
import { wheelman } from './wheelman.js';
import { mole } from './mole.js';
import { capo } from './capo.js';
import { strangler } from './strangler.js';
import { disguiser } from './disguiser.js';

import { arsonist } from './arsonist.js';
import { maniac } from './maniac.js';
import { werewolf } from './werewolf.js';
import { survivor } from './survivor.js';
import { jester } from './jester.js';
import { witch } from './witch.js';
import { executioner } from './executioner.js';
import { cultist } from './cultist.js';
import { plaguebearer } from './plaguebearer.js';

export const ALL_ROLES: RoleConfig[] = [
  // Town (18)
  civilian, sheriff, doctor, lawyer,
  bodyguard, mayor, vigilante,
  busDriver, veteran, spy, watcher,
  priest, detective, escort, monk, coroner,
  jailer, tracker,
  // Mafia (12)
  don, goon, consigliere, framer, janitor, hitman,
  saboteur, wheelman, mole, capo,
  strangler, disguiser,
  // Solo (9)
  arsonist, maniac, werewolf, survivor, jester, witch, executioner,
  cultist, plaguebearer,
];

export const ROLE_BY_ID: Record<string, RoleConfig> = Object.fromEntries(
  ALL_ROLES.map((r) => [r.id, r]),
);

// Default lineup — positional buildOrder. The server slices to seat count
// before shuffling, so the *order* here defines what short games look like:
// every prefix in [4..8] must include a killer. The full 8-slot composition
// is the original 5T + 2M + 1S "classic" — we just rearranged so mafia/solo
// aren't all stacked at the tail.
export const DEFAULT_LINEUP: string[] = [
  'civilian', 'sheriff', 'doctor', 'goon',
  'civilian', 'don', 'civilian', 'arsonist',
];
